import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import DataTable from '../../components/DataTable';
import ColumnConfig from '../../components/ColumnConfig';
import LockedAccountsPanel from '../../components/admin/users/LockedAccountsPanel';
import api from '../../services/api';
import { toast } from 'sonner';
import { 
  Users, 
  Upload, 
  Mail, 
  UserCheck,
  UserX,
  Shield,
  GraduationCap,
  Briefcase,
  User,
  Target,
  Download,
  Send,
  Trash2,
  Pencil,
  CheckCircle,
  AlertCircle,
  FileSpreadsheet,
  ChevronDown,
  FileText,
  Key,
  Copy,
  UserPlus,
  Eye
} from 'lucide-react';

const ROLE_CONFIG = {
  admin: { label: 'Admin', plural: 'Admins', icon: Shield, color: 'bg-red-100 text-red-700' },
  coach: { label: 'Coach', plural: 'Coaches', icon: GraduationCap, color: 'bg-purple-100 text-purple-700' },
  staff: { label: 'Staff', plural: 'Staff', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  parent: { label: 'Parent', plural: 'Parents', icon: User, color: 'bg-green-100 text-green-700' },
  customer: { label: 'Customer', plural: 'Customers', icon: Target, color: 'bg-amber-100 text-amber-700' },
  player: { label: 'Player', plural: 'Players', icon: User, color: 'bg-cyan-100 text-cyan-700' }
};

const DOCUMENT_TYPES = [
  { value: 'privacy_policy', label: 'Privacy Policy' },
  { value: 'shipping_policy', label: 'Shipping Policy' },
  { value: 'terms_of_service', label: 'Terms of Service' },
  { value: 'return_refund_policy', label: 'Return & Refund Policy' },
  { value: 'do_not_sell_info', label: 'Do Not Sell My Personal Information' }
];

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  
  // Dialogs
  const [uploadDialog, setUploadDialog] = useState(false);
  const [roleDialog, setRoleDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [bulkEmailDialog, setBulkEmailDialog] = useState(false);
  const [passwordResetDialog, setPasswordResetDialog] = useState(false);
  const [addUserDialog, setAddUserDialog] = useState(false);
  const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
  const [bulkStatusTarget, setBulkStatusTarget] = useState('inactive'); // 'active' | 'inactive'
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  
  // Selected user for actions
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  
  // Password reset state
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [customPassword, setCustomPassword] = useState('');
  
  // Add user state
  const [newUserForm, setNewUserForm] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'parent',
    send_welcome_email: true
  });
  const [addingUser, setAddingUser] = useState(false);
  
  // Upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  
  // Bulk email state
  const [bulkEmailType, setBulkEmailType] = useState('login'); // 'login' or 'document'
  const [selectedDocument, setSelectedDocument] = useState('');
  const [sendingEmails, setSendingEmails] = useState(false);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);
    
    try {
      const response = await api.post('/admin/users/bulk-upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadResult(response.data);
      toast.success(`Created ${response.data.created} users`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleChangeRole = async () => {
    if (!selectedUser || !newRole) return;
    
    try {
      await api.put(`/admin/users/${selectedUser.id}/role`, null, {
        params: { new_role: newRole }
      });
      toast.success('User role updated');
      setRoleDialog(false);
      setSelectedUser(null);
      setNewRole('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/admin/users/${selectedUser.id}`);
      toast.success('User deleted');
      setDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    setResettingPassword(true);
    try {
      const payload = customPassword ? { new_password: customPassword } : {};
      const response = await api.post(`/admin/users/${selectedUser.id}/reset-password`, payload);
      setResetPasswordResult(response.data);
      toast.success('Password reset successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResettingPassword(false);
    }
  };

  const openPasswordResetDialog = (user) => {
    setSelectedUser(user);
    setResetPasswordResult(null);
    setCustomPassword('');
    setPasswordResetDialog(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleBulkEmail = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }
    
    setSendingEmails(true);
    try {
      if (bulkEmailType === 'login') {
        // Send login credentials
        const response = await api.post('/admin/users/send-login-emails', selectedUsers);
        toast.success(`Sent ${response.data.sent} login emails`);
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} emails failed to send`);
        }
      } else if (bulkEmailType === 'document') {
        // Send document update notification
        if (!selectedDocument) {
          toast.error('Please select a document');
          setSendingEmails(false);
          return;
        }
        const response = await api.post(`/admin/documents/${selectedDocument}/notify`, selectedUsers, {
          params: { notify_all: false }
        });
        toast.success(`Sent ${response.data.sent} document update emails`);
        if (response.data.failed > 0) {
          toast.warning(`${response.data.failed} emails failed to send`);
        }
      }
      setBulkEmailDialog(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingEmails(false);
    }
  };

  const openBulkEmailDialog = (type) => {
    setBulkEmailType(type);
    setSelectedDocument('');
    setBulkEmailDialog(true);
  };

  const openBulkStatusDialog = (target) => {
    setBulkStatusTarget(target);
    setBulkStatusDialog(true);
  };

  const handleBulkStatus = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }
    setBulkProcessing(true);
    try {
      // iter258 — send `is_active` (bool) canonical.
      const response = await api.post('/admin/users/bulk-status', {
        user_ids: selectedUsers,
        is_active: bulkStatusTarget === 'active',
        status: bulkStatusTarget,  // kept for backward-compat
      });
      const verb = bulkStatusTarget === 'active' ? 'Activated' : 'Deactivated';
      toast.success(`${verb} ${response.data.updated} user(s)`);
      if (response.data.skipped_self) {
        toast.warning('Your own account was skipped');
      }
      setBulkStatusDialog(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }
    setBulkProcessing(true);
    try {
      const response = await api.post('/admin/users/bulk-delete', {
        user_ids: selectedUsers
      });
      toast.success(`Deleted ${response.data.deleted} user(s)`);
      if (response.data.skipped_self) {
        toast.warning('Your own account was skipped');
      }
      setBulkDeleteDialog(false);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleSendAllPendingLogins = async () => {
    const pendingIds = users.filter(u => u.pending_login_email && u.is_active !== false).map(u => u.id);
    if (pendingIds.length === 0) {
      toast.info('No pending login emails to send');
      return;
    }
    setBulkProcessing(true);
    try {
      const response = await api.post('/admin/users/send-login-emails', pendingIds);
      toast.success(`Sent ${response.data.sent} login email(s)`);
      if (response.data.failed > 0) {
        toast.warning(`${response.data.failed} failed to send`);
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send pending emails');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleClearPendingFlag = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select users first');
      return;
    }
    setBulkProcessing(true);
    try {
      const response = await api.post('/admin/users/bulk-clear-pending-email', {
        user_ids: selectedUsers
      });
      toast.success(`Cleared pending flag on ${response.data.cleared} user(s)`);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear pending flag');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const filteredUsers = getFilteredUsers();
      setSelectedUsers(filteredUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const getFilteredUsers = () => {
    if (activeTab === 'all') return users;
    if (activeTab === 'pending_email') {
      return users.filter(u => u.pending_login_email === true);
    }
    if (activeTab === 'inactive') {
      return users.filter(u => u.is_active === false);
    }
    return users.filter(u => u.role === activeTab);
  };

  const roleCounts = useMemo(() => {
    const counts = { all: users.length, pending_email: 0, inactive: 0 };
    users.forEach(u => {
      counts[u.role] = (counts[u.role] || 0) + 1;
      if (u.pending_login_email) counts.pending_email++;
      if (u.is_active === false) counts.inactive++;
    });
    return counts;
  }, [users]);

  const getProfileUrl = (row) => {
    if (row.role === 'customer') return `/admin/customers/${row.id}`;
    if (row.role === 'parent') return `/admin/parents/${row.id}`;
    if (row.role === 'coach') return `/admin/coaches/${row.id}`;
    return null;
  };

  const columns = [
    {
      key: 'select',
      label: '',
      render: (row) => (
        <Checkbox
          checked={selectedUsers.includes(row.id)}
          onCheckedChange={(checked) => handleSelectUser(row.id, checked)}
          data-testid={`select-user-${row.id}`}
        />
      )
    },
    {
      key: 'name',
      label: 'User',
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (row) => {
        const RoleIcon = ROLE_CONFIG[row.role]?.icon || User;
        const profileUrl = getProfileUrl(row);
        return (
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
              ROLE_CONFIG[row.role]?.color || 'bg-slate-100 text-slate-600'
            }`}>
              <RoleIcon className="h-5 w-5" />
            </div>
            <div>
              {profileUrl ? (
                <button
                  type="button"
                  onClick={() => navigate(profileUrl)}
                  className="font-medium text-slate-900 hover:text-blue-600 hover:underline text-left"
                  data-testid={`view-profile-${row.id}`}
                >
                  {row.name}
                </button>
              ) : (
                <p className="font-medium text-slate-900">{row.name}</p>
              )}
              <p className="text-sm text-slate-500">{row.email}</p>
            </div>
          </div>
        );
      }
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: Object.entries(ROLE_CONFIG).map(([value, config]) => ({
        value,
        label: config.label
      })),
      render: (row) => {
        const config = ROLE_CONFIG[row.role] || ROLE_CONFIG.customer;
        const Icon = config.icon;
        return (
          <Badge className={`${config.color} flex items-center gap-1 w-fit`}>
            <Icon className="h-3 w-3" />
            {config.label}
          </Badge>
        );
      }
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => row.phone || <span className="text-slate-400">-</span>
    },
    {
      key: 'pending_login_email',
      label: 'Login Email',
      sortable: true,
      render: (row) => {
        if (row.pending_login_email) {
          return (
            <Badge className="bg-amber-100 text-amber-700 flex items-center gap-1 w-fit">
              <AlertCircle className="h-3 w-3" />
              Pending
            </Badge>
          );
        }
        if (row.login_email_sent_at) {
          return (
            <Badge className="bg-green-100 text-green-700 flex items-center gap-1 w-fit">
              <CheckCircle className="h-3 w-3" />
              Sent
            </Badge>
          );
        }
        return <span className="text-slate-400">-</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'inactive', label: 'Inactive' }
      ],
      render: (row) => {
        const isInactive = row.is_active === false;
        return (
          <Badge className={`${isInactive ? 'bg-slate-200 text-slate-600' : 'bg-green-100 text-green-700'} flex items-center gap-1 w-fit`}>
            {isInactive ? <UserX className="h-3 w-3" /> : <UserCheck className="h-3 w-3" />}
            {isInactive ? 'Inactive' : 'Active'}
          </Badge>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row) => new Date(row.created_at).toLocaleDateString()
    }
  ];

  const actions = (row) => {
    const profileUrl = getProfileUrl(row);
    return (
      <div className="flex items-center gap-1">
        {profileUrl && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(profileUrl)}
            title="View Profile"
            data-testid={`view-profile-btn-${row.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openPasswordResetDialog(row)}
          title="Reset Password"
          data-testid={`reset-password-${row.id}`}
        >
          <Key className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedUser(row);
            setNewRole(row.role);
            setRoleDialog(true);
          }}
          title="Change Role"
          data-testid={`edit-role-${row.id}`}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setSelectedUser(row);
            setDeleteDialog(true);
          }}
          className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
          title="Delete User"
          data-testid={`delete-user-${row.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  const downloadSampleCSV = () => {
    const sample = 'name,email,phone,role\nJohn Doe,john@example.com,555-1234,parent\nJane Smith,jane@example.com,555-5678,coach\nBob Wilson,bob@example.com,,customer';
    const blob = new Blob([sample], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'users_sample.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newUserForm.name || !newUserForm.email) {
      toast.error('Name and email are required');
      return;
    }
    
    setAddingUser(true);
    try {
      const _response = await api.post('/admin/users/create', {
        name: newUserForm.name,
        email: newUserForm.email,
        phone: newUserForm.phone || null,
        role: newUserForm.role,
        send_welcome_email: newUserForm.send_welcome_email
      });
      
      toast.success(`User created${newUserForm.send_welcome_email ? ' and welcome email sent' : ''}`);
      setAddUserDialog(false);
      setNewUserForm({
        name: '',
        email: '',
        phone: '',
        role: 'parent',
        send_welcome_email: true
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setAddingUser(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="User Management">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="User Management">
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-slate-600">Manage all application users and their roles</p>
          <div className="flex flex-wrap items-center gap-2">
            {/* Bulk Actions Dropdown */}
            {selectedUsers.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    data-testid="bulk-actions-btn"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    Bulk Actions ({selectedUsers.length})
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Send Email</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => openBulkEmailDialog('login')}
                    data-testid="bulk-login-emails"
                  >
                    <Key className="h-4 w-4 mr-2" />
                    Send Login Credentials
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => openBulkEmailDialog('document')}
                    data-testid="bulk-document-emails"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Send Document Update
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleClearPendingFlag}
                    data-testid="bulk-clear-pending-email"
                  >
                    <CheckCircle className="h-4 w-4 mr-2 text-slate-600" />
                    Mark Pending as Sent (no email)
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuLabel>Account Status</DropdownMenuLabel>
                  <DropdownMenuItem
                    onClick={() => openBulkStatusDialog('active')}
                    data-testid="bulk-activate-users"
                  >
                    <UserCheck className="h-4 w-4 mr-2 text-green-600" />
                    Activate Users
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => openBulkStatusDialog('inactive')}
                    data-testid="bulk-deactivate-users"
                  >
                    <UserX className="h-4 w-4 mr-2 text-amber-600" />
                    Deactivate Users
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => setBulkDeleteDialog(true)}
                    className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                    data-testid="bulk-delete-users"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Users
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <ColumnConfig
              columns={columns.filter(c => c.key !== 'select')}
              visibleColumns={visibleColumns}
              onColumnChange={setVisibleColumns}
              storageKey="admin-users-columns"
              requiredColumns={['name']}
            />
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const response = await api.get('/admin/users/export', { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([response.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.setAttribute('download', 'users_export.csv');
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success('Users exported');
                } catch (error) {
                  toast.error('Failed to export users');
                }
              }}
              data-testid="export-users-btn"
            >
              <Download className="h-4 w-4 mr-1" />
              Export
            </Button>
            <Button
              onClick={() => {
                setUploadDialog(true);
                setUploadFile(null);
                setUploadResult(null);
              }}
              variant="outline"
              data-testid="upload-users-btn"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Users
            </Button>
            <Button
              onClick={() => {
                setAddUserDialog(true);
                setNewUserForm({
                  name: '',
                  email: '',
                  phone: '',
                  role: 'parent',
                  send_welcome_email: true
                });
              }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-user-btn"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-l-4 border-l-slate-500">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Users</p>
              <p className="font-heading text-2xl font-bold">{roleCounts.all}</p>
            </CardContent>
          </Card>
          {Object.entries(ROLE_CONFIG).map(([role, config]) => {
            const Icon = config.icon;
            return (
              <Card key={role} className={`border-l-4 ${config.color.replace('bg-', 'border-l-').replace('-100', '-500')}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Icon className="h-4 w-4" />
                    {config.plural}
                  </div>
                  <p className="font-heading text-2xl font-bold">{roleCounts[role] || 0}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Users Table with Tabs */}
        <Card className="border border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="h-5 w-5 text-slate-600" />
              All Users
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* iter318 wave 9 — Locked accounts panel. Renders inline
                only when >0 users are currently locked; hidden otherwise
                so the standard Users page stays uncluttered. */}
            <LockedAccountsPanel onUnlocked={() => fetchUsers?.()} />
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelectedUsers([]); }} className="w-full">
              <TabsList className="flex flex-wrap gap-1 h-auto mb-4">
                <TabsTrigger value="all" data-testid="tab-all">
                  All ({roleCounts.all})
                </TabsTrigger>
                <TabsTrigger value="pending_email" data-testid="tab-pending-email" className="text-amber-600">
                  <Mail className="h-4 w-4 mr-1" />
                  Pending Email ({roleCounts.pending_email})
                </TabsTrigger>
                <TabsTrigger value="inactive" data-testid="tab-inactive" className="text-slate-500">
                  <UserX className="h-4 w-4 mr-1" />
                  Inactive ({roleCounts.inactive})
                </TabsTrigger>
                {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                  const Icon = config.icon;
                  return (
                    <TabsTrigger key={role} value={role} data-testid={`tab-${role}`}>
                      <Icon className="h-4 w-4 mr-1" />
                      {config.plural} ({roleCounts[role] || 0})
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              <div className="mb-4 flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 text-sm text-slate-600">
                  <Checkbox
                    checked={selectedUsers.length === getFilteredUsers().length && getFilteredUsers().length > 0}
                    onCheckedChange={handleSelectAll}
                    data-testid="select-all-users"
                  />
                  Select All ({getFilteredUsers().length})
                </label>
                {activeTab === 'pending_email' && roleCounts.pending_email > 0 && (
                  <Button
                    size="sm"
                    onClick={handleSendAllPendingLogins}
                    disabled={bulkProcessing}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="send-all-pending-btn"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {bulkProcessing ? 'Sending...' : `Send All Pending Logins (${roleCounts.pending_email})`}
                  </Button>
                )}
              </div>

              <TabsContent value={activeTab}>
                <DataTable
                  data={getFilteredUsers()}
                  columns={columns}
                  visibleColumns={visibleColumns.length > 0 ? ['select', ...visibleColumns] : undefined}
                  actions={actions}
                  emptyIcon={Users}
                  emptyMessage={activeTab === 'all' ? "No users found." : `No ${activeTab} users.`}
                  searchable={true}
                  searchFields={['name', 'email', 'phone']}
                  testIdPrefix="users"
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Upload className="h-5 w-5 text-blue-600" />
                Upload Users
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 mb-2">CSV Format</h4>
                <p className="text-sm text-blue-700 mb-2">
                  Upload a CSV file with the following columns:
                </p>
                <code className="block bg-blue-100 rounded px-2 py-1 text-xs text-blue-800">
                  name, email, phone, role
                </code>
                <p className="text-xs text-blue-600 mt-2">
                  Valid roles: admin, coach, staff, parent, customer
                </p>
                <p className="text-xs text-blue-600">
                  Passwords will be auto-generated and sent via email.
                </p>
                <Button
                  variant="link"
                  size="sm"
                  onClick={downloadSampleCSV}
                  className="text-blue-600 p-0 h-auto mt-2"
                >
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Download Sample CSV
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Select CSV File</Label>
                <Input
                  type="file"
                  accept=".csv"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  data-testid="upload-file-input"
                />
              </div>

              {uploadResult && (
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium">Upload Results</h4>
                  <div className="grid grid-cols-3 gap-2 text-sm">
                    <div className="text-center p-2 bg-green-100 rounded">
                      <p className="text-green-700 font-bold">{uploadResult.created}</p>
                      <p className="text-green-600 text-xs">Created</p>
                    </div>
                    <div className="text-center p-2 bg-amber-100 rounded">
                      <p className="text-amber-700 font-bold">{uploadResult.skipped}</p>
                      <p className="text-amber-600 text-xs">Skipped</p>
                    </div>
                    <div className="text-center p-2 bg-red-100 rounded">
                      <p className="text-red-700 font-bold">{uploadResult.errors}</p>
                      <p className="text-red-600 text-xs">Errors</p>
                    </div>
                  </div>
                  {uploadResult.details?.errors?.length > 0 && (
                    <div className="mt-2 text-xs text-red-600">
                      {uploadResult.details.errors.map((e, i) => (
                        <p key={i}>Row {e.row}: {e.error}</p>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialog(false)}>
                Close
              </Button>
              <Button
                onClick={handleUpload}
                disabled={!uploadFile || uploading}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="submit-upload-btn"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Role Dialog */}
        <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Change User Role</DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-4 mt-2">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="font-medium">{selectedUser.name}</p>
                  <p className="text-sm text-slate-500">{selectedUser.email}</p>
                </div>

                <div className="space-y-2">
                  <Label>New Role</Label>
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(ROLE_CONFIG).map(([role, config]) => {
                        const Icon = config.icon;
                        return (
                          <SelectItem key={role} value={role}>
                            <span className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {config.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setRoleDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleChangeRole} data-testid="confirm-role-change-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-red-600">Delete User</DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-4 mt-2">
                <p className="text-slate-600">
                  Are you sure you want to delete <strong>{selectedUser.name}</strong>? This action cannot be undone.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  This will also delete any associated profiles and data for this user.
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleDeleteUser} 
                className="bg-red-500 hover:bg-red-600"
                data-testid="confirm-delete-btn"
              >
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Email Dialog */}
        <Dialog open={bulkEmailDialog} onOpenChange={setBulkEmailDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-600" />
                {bulkEmailType === 'login' ? 'Send Login Credentials' : 'Send Document Update'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-2">
              <p className="text-slate-600">
                Send emails to <strong>{selectedUsers.length}</strong> selected user(s).
              </p>

              {bulkEmailType === 'login' ? (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  <Key className="h-4 w-4 inline mr-1" />
                  New passwords will be generated for users who haven't received their credentials yet.
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                    <FileText className="h-4 w-4 inline mr-1" />
                    Select which document update notification to send.
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Select Document</Label>
                    <Select value={selectedDocument} onValueChange={setSelectedDocument}>
                      <SelectTrigger data-testid="document-select">
                        <SelectValue placeholder="Choose a document..." />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TYPES.map((doc) => (
                          <SelectItem key={doc.value} value={doc.value}>
                            {doc.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkEmailDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleBulkEmail}
                disabled={sendingEmails || (bulkEmailType === 'document' && !selectedDocument)}
                className="bg-blue-500 hover:bg-blue-600"
                data-testid="confirm-send-emails-btn"
              >
                {sendingEmails ? 'Sending...' : `Send ${bulkEmailType === 'login' ? 'Login Emails' : 'Notification'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog open={passwordResetDialog} onOpenChange={(open) => {
          setPasswordResetDialog(open);
          if (!open) {
            setResetPasswordResult(null);
            setCustomPassword('');
          }
        }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <Key className="h-5 w-5 text-amber-600" />
                Reset User Password
              </DialogTitle>
            </DialogHeader>
            
            {!resetPasswordResult ? (
              <div className="space-y-4 mt-2">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedUser?.name}</p>
                  <p className="text-sm text-slate-600">{selectedUser?.email}</p>
                  <p className="text-xs text-slate-500 capitalize mt-1">{selectedUser?.role}</p>
                </div>
                
                <div className="space-y-2">
                  <Label>New Password (optional)</Label>
                  <Input
                    type="text"
                    value={customPassword}
                    onChange={(e) => setCustomPassword(e.target.value)}
                    placeholder="Leave empty to generate random password"
                    data-testid="custom-password-input"
                  />
                  <p className="text-xs text-slate-500">
                    If left empty, a secure 12-character password will be generated.
                  </p>
                </div>
                
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  <AlertCircle className="h-4 w-4 inline mr-1" />
                  The user will need this password to log in. Share it securely.
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setPasswordResetDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResetPassword}
                    disabled={resettingPassword}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="confirm-reset-password-btn"
                  >
                    {resettingPassword ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-4 mt-2">
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 text-green-700 mb-2">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Password Reset Successfully</span>
                  </div>
                  <p className="text-sm text-green-600 mb-3">
                    Share this password securely with the user.
                  </p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                      <span className="text-sm text-slate-600">User:</span>
                      <span className="font-medium">{resetPasswordResult.user_email}</span>
                    </div>
                    <div className="flex items-center justify-between p-2 bg-white rounded border">
                      <span className="text-sm text-slate-600">New Password:</span>
                      <div className="flex items-center gap-2">
                        <code className="font-mono bg-slate-100 px-2 py-1 rounded text-sm">
                          {resetPasswordResult.temporary_password}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(resetPasswordResult.temporary_password)}
                          data-testid="copy-password-btn"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
                
                <p className="text-xs text-slate-500 text-center">
                  Advise the user to change their password after logging in.
                </p>
                
                <DialogFooter>
                  <Button onClick={() => setPasswordResetDialog(false)}>
                    Done
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add User Dialog */}
        <Dialog open={addUserDialog} onOpenChange={setAddUserDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                <UserPlus className="h-5 w-5" />
                Add New User
              </DialogTitle>
              <DialogDescription>
                Create a new user account. A welcome email with login credentials will be sent.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddUser} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name <span className="text-red-500">*</span></Label>
                <Input
                  value={newUserForm.name}
                  onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="add-user-name"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={newUserForm.email}
                  onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                  data-testid="add-user-email"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Phone (Optional)</Label>
                <Input
                  value={newUserForm.phone}
                  onChange={(e) => setNewUserForm({ ...newUserForm, phone: e.target.value })}
                  placeholder="555-1234"
                  data-testid="add-user-phone"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Role <span className="text-red-500">*</span></Label>
                <Select
                  value={newUserForm.role}
                  onValueChange={(value) => setNewUserForm({ ...newUserForm, role: value })}
                >
                  <SelectTrigger data-testid="add-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="coach">Coach</SelectItem>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="customer">Customer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-2">
                <Checkbox
                  id="send_welcome_email"
                  checked={newUserForm.send_welcome_email}
                  onCheckedChange={(checked) => setNewUserForm({ ...newUserForm, send_welcome_email: checked })}
                  data-testid="add-user-send-email"
                />
                <label htmlFor="send_welcome_email" className="text-sm text-slate-600 cursor-pointer">
                  Send welcome email with temporary password
                </label>
              </div>
              
              <DialogFooter className="pt-4">
                <Button type="button" variant="outline" onClick={() => setAddUserDialog(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={addingUser} data-testid="add-user-submit">
                  {addingUser ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bulk Status Change Dialog */}
        <Dialog open={bulkStatusDialog} onOpenChange={setBulkStatusDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl flex items-center gap-2">
                {bulkStatusTarget === 'active' ? (
                  <UserCheck className="h-5 w-5 text-green-600" />
                ) : (
                  <UserX className="h-5 w-5 text-amber-600" />
                )}
                {bulkStatusTarget === 'active' ? 'Activate Users' : 'Deactivate Users'}
              </DialogTitle>
              <DialogDescription>
                {bulkStatusTarget === 'active'
                  ? `Reactivate ${selectedUsers.length} user(s)? They will be able to log in again.`
                  : `Deactivate ${selectedUsers.length} user(s)? They will be unable to log in until reactivated.`}
              </DialogDescription>
            </DialogHeader>
            <div className={`rounded-lg p-3 text-sm ${
              bulkStatusTarget === 'active'
                ? 'bg-green-50 border border-green-200 text-green-700'
                : 'bg-amber-50 border border-amber-200 text-amber-700'
            }`}>
              <AlertCircle className="h-4 w-4 inline mr-1" />
              {bulkStatusTarget === 'active'
                ? 'Their accounts and data remain intact — only login access is restored.'
                : 'Their data is preserved. They simply cannot log in. You can reactivate them anytime.'}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkStatusDialog(false)} disabled={bulkProcessing}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkStatus}
                disabled={bulkProcessing}
                className={bulkStatusTarget === 'active'
                  ? 'bg-green-600 hover:bg-green-700'
                  : 'bg-amber-500 hover:bg-amber-600'}
                data-testid="confirm-bulk-status-btn"
              >
                {bulkProcessing
                  ? 'Updating...'
                  : (bulkStatusTarget === 'active' ? 'Activate' : 'Deactivate')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Delete Confirmation Dialog */}
        <Dialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-rose-600 flex items-center gap-2">
                <Trash2 className="h-5 w-5" />
                Delete Users
              </DialogTitle>
              <DialogDescription>
                Permanently delete <strong>{selectedUsers.length}</strong> user(s)? This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm text-rose-700">
              <AlertCircle className="h-4 w-4 inline mr-1" />
              This will also remove their role-specific profiles (coach, staff, parent). Associated bookings, invoices, and historical records will be retained but orphaned.
            </div>
            <p className="text-xs text-slate-500">
              If you only want to revoke login access, use <strong>Deactivate</strong> instead.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteDialog(false)} disabled={bulkProcessing}>
                Cancel
              </Button>
              <Button
                onClick={handleBulkDelete}
                disabled={bulkProcessing}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="confirm-bulk-delete-btn"
              >
                {bulkProcessing ? 'Deleting...' : `Delete ${selectedUsers.length} User(s)`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default UserManagement;
